"use client";

import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Save, TrendingDown, TrendingUp } from "lucide-react";
import MetricCard from "./MetricCard";
import { money, n, pct } from "./utils";

export default function ROITab({ state }) {
  const oneTime = n(state.roiOneTimeInvestment);
  const monthlyInvestment = n(state.roiMonthlyInvestment);
  const period = Math.max(n(state.roiPeriodMonths), 1);
  const additionalIncome = n(state.roiOtherMonthlyGain);
  const monthlySaving = n(state.roiMonthlySaving);
  const totalInvestment = oneTime + monthlyInvestment * period;
  const totalReturn = (additionalIncome + monthlySaving) * period;
  const netGain = totalReturn - totalInvestment;
  const roi = totalInvestment ? (netGain / totalInvestment) * 100 : 0;

  const chartData = [
    { name: "Investment", value: totalInvestment, fill: "#334155" },
    { name: "Return", value: totalReturn, fill: "#2563eb" },
    { name: "Net result", value: netGain, fill: netGain >= 0 ? "#16a34a" : "#dc2626" },
  ];

  return (
    <div className="space-y-4">
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Investment return</h2>
            <p className="mt-1 text-sm text-slate-500">A simple view of an initiative’s cost, financial return, and savings.</p>
          </div>
          <button
            type="button"
            onClick={state.saveRoiInitiative}
            disabled={state.roiSaving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#D67C2C] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#c06a25] disabled:cursor-not-allowed disabled:opacity-70"
          >
            <Save className="h-4 w-4" />
            {state.roiSaving ? "Saving..." : "Save ROI"}
          </button>
        </div>
        {state.error && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700">
            {state.error}
          </div>
        )}
        {state.saveMessage && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
            {state.saveMessage}
          </div>
        )}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Initiative" value={state.roiInvestmentName} onChange={state.setRoiInvestmentName} />
          <Field label="One-time cost" type="number" value={state.roiOneTimeInvestment} onChange={(value) => state.setRoiOneTimeInvestment(Number(value))} />
          <Field label="Monthly cost" type="number" value={state.roiMonthlyInvestment} onChange={(value) => state.setRoiMonthlyInvestment(Number(value))} />
          <Field label="Review period (months)" type="number" value={state.roiPeriodMonths} onChange={(value) => state.setRoiPeriodMonths(Number(value))} />
          <Field label="Additional monthly income" type="number" value={state.roiOtherMonthlyGain} onChange={(value) => state.setRoiOtherMonthlyGain(Number(value))} />
          <Field label="Monthly cost saving" type="number" value={state.roiMonthlySaving} onChange={(value) => state.setRoiMonthlySaving(Number(value))} />
        </div>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard variant="blue" label="Total investment" value={money(totalInvestment)} note={`Over ${period} month${period === 1 ? "" : "s"}.`} />
        <MetricCard variant="green" label="Financial return" value={money(totalReturn)} note="Additional income plus savings." />
        <MetricCard variant={netGain >= 0 ? "green" : "red"} label="Net result" value={money(netGain)} note="Return less total investment." />
        <MetricCard variant="blue" label="ROI" value={pct(roi)} note="Net result ÷ investment." />
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <h2 className="text-lg font-semibold text-slate-800">Decision summary</h2>
          <div className={`mt-4 flex gap-3 rounded-lg p-4 ${netGain >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
            {netGain >= 0 ? <TrendingUp className="w-5 h-5 mt-0.5 shrink-0" /> : <TrendingDown className="w-5 h-5 mt-0.5 shrink-0" />}
            <p className="text-sm leading-6">
              {netGain >= 0
                ? `${state.roiInvestmentName || "This initiative"} is expected to return ${money(netGain)} above its cost over ${period} month${period === 1 ? "" : "s"}.`
                : `${state.roiInvestmentName || "This initiative"} is expected to fall short by ${money(Math.abs(netGain))}. Review the cost or expected return before continuing.`}
            </p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <h2 className="text-lg font-semibold text-slate-800">Investment versus return</h2>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={money} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={72} />
                <Tooltip formatter={money} />
                <Bar dataKey="value" radius={[5, 5, 0, 0]}>{chartData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return <label className="block text-sm font-medium text-slate-700">{label}<input type={type} value={value} min={type === "number" ? 0 : undefined} onChange={(event) => onChange(event.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200" /></label>;
}
